/**
 * Inpatient encounters in ChartMind: the setting choice, the Events list, the Daily Plan
 * comments, and what all of it adds to the note request.
 *
 * Ported from ChartMind-7 (HealthDeskMinusOne), cut down to one set of instructions for every
 * specialty. Outpatient encounters never touch anything in here.
 */

import { format, isValid, parseISO } from "date-fns";

export const ENCOUNTER_SETTINGS = {
  OUTPATIENT: "outpatient",
  INPATIENT: "inpatient",
};

export const isInpatient = (setting) => setting === ENCOUNTER_SETTINGS.INPATIENT;

/**
 * Inpatient versions of ChartMind's prompts, by base prompt id. Created per environment by
 * scripts/seedInpatientPrompts.js; an environment without them falls back to the base prompt.
 */
export const INPATIENT_PROMPT_IDS = {
  "chartmind-diagnosis": "chartmind-diagnosis-inpatient",
  "chartmind-treatment": "chartmind-treatment-inpatient",
  "chartmind-discharge": "chartmind-discharge-inpatient",
  "chartmind-notes": "chartmind-notes-inpatient",
  "chartmind-pearls": "chartmind-pearls-inpatient",
};

/** Prompt options for a step: the inpatient prompt with the base as fallback, or nothing. */
export const promptForSetting = (basePromptId, setting) =>
  isInpatient(setting)
    ? { promptId: INPATIENT_PROMPT_IDS[basePromptId], fallbackPromptId: basePromptId }
    : {};

/** Unknown or missing values read as outpatient, which is how every older session was made. */
export const normalizeEncounterSetting = (value) =>
  isInpatient(value) ? ENCOUNTER_SETTINGS.INPATIENT : ENCOUNTER_SETTINGS.OUTPATIENT;

// ============================================================================
// Events
// ============================================================================

export const INPATIENT_EVENT_OPTIONS = [
  "Hypoglycemic Episode",
  "Hypoxia",
  "Altered Mental Status",
  "Fall",
  "Chest Pain",
  "Shortness of Breath",
  "Hypotension",
  "Hypertension",
  "Tachycardia",
  "Bradycardia",
  "Arrhythmia",
  "Fever",
  "Seizure",
  "Syncope",
  "Unresponsiveness",
  "Respiratory Distress",
  "Acute Bleeding",
  "Aspiration",
  "Oliguria",
  "Acute Pain",
  "Agitation",
  "Delirium",
  "Nausea and Vomiting",
  "Transfusion Reaction",
  "Rapid Response",
  "Code Blue",
];

/** The time field's format: a local date and time, as a datetime-local input reads and writes. */
export const EVENT_TIME_FORMAT = "yyyy-MM-dd'T'HH:mm";

/** When the event happened, or null when it has none or it does not parse. */
export const parseEventTime = (occurredAt) => {
  if (!occurredAt) return null;
  const date = parseISO(String(occurredAt));
  return isValid(date) ? date : null;
};

/**
 * Events are only ever entered by the clinician. The time is when it happened, which the form
 * fills with the time of entry because Events notes are written as things happen. The clinician
 * corrects it when the event was earlier, or clears it.
 */
export const createInpatientEvent = (name, comment = "", occurredAt = "") => ({
  // eslint-disable-next-line no-restricted-properties -- Non-security: local list-item id.
  id: `event-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: String(name || "").trim(),
  comment: String(comment || ""),
  occurredAt: parseEventTime(occurredAt) ? String(occurredAt) : "",
});

export const normalizeInpatientEvents = (value) =>
  (Array.isArray(value) ? value : [])
    .map((row) => ({
      id: String(row?.id || "").trim(),
      name: String(row?.name || "").trim(),
      comment: String(row?.comment || ""),
      occurredAt: parseEventTime(row?.occurredAt) ? String(row.occurredAt) : "",
    }))
    .filter((row) => row.id && row.name);

// ============================================================================
// Daily Plan
// ============================================================================

/**
 * A checked item is pending when the clinician has neither marked it reassuring nor written
 * anything about it. A comment that says the item is still open ("due Tuesday") is left to the
 * model, which is told to list those under Pending as well.
 */
export const isPendingDailyPlanItem = (item, { comments = {}, reassuring = {} } = {}) =>
  !reassuring[item.id] && !String(comments[item.id] || "").trim();

/** Names of the checked items still pending. */
export const pendingDailyPlanNames = (checkedItems = [], marks = {}) =>
  checkedItems.filter((item) => isPendingDailyPlanItem(item, marks)).map((item) => item.name);

/**
 * The note's Pending section must list every pending Daily Plan item. The model is told to, and
 * sometimes drops one anyway, so any it left out are added back after the note comes in.
 * Anything else it put there (a blood culture mentioned only in the narrative) is kept.
 */
const normalizeLine = (line) =>
  line.replace(/^[\s\-*•\d.)]+/, "").trim().toLowerCase();

/** True when some line of the section starts with the item's name (then ends or punctuation). */
const sectionLists = (lines, name) => {
  const target = name.trim().toLowerCase();
  return lines.some(
    (line) => line === target || (line.startsWith(target) && /^[^a-z0-9]/.test(line.slice(target.length))),
  );
};

export const ensurePendingInNote = (sections, pendingNames = []) => {
  if (!sections || !pendingNames.length) return sections;
  const current = String(sections.pending || "").trim();
  // Line by line, not anywhere in the text: "Chest X-ray" is not listed just because
  // "Repeat chest X-ray" is.
  const lines = current.split("\n").map(normalizeLine).filter(Boolean);
  const missing = pendingNames.filter((name) => !sectionLists(lines, name));
  if (!missing.length) return sections;
  const added = missing.map((name) => `- ${name}`).join("\n");
  return { ...sections, pending: current ? `${current}\n${added}` : added };
};

const dailyPlanLine = (item, { comments = {}, reassuring = {} }) => {
  const extra = [reassuring[item.id] ? "reassuring" : "", String(comments[item.id] || "").trim()]
    .filter(Boolean)
    .join("; ");
  return `- ${item.name}${extra ? `: ${extra}` : ""}`;
};

// ============================================================================
// Note
// ============================================================================

/**
 * Inpatient notes are an admission H&P, a next-day progress note, or an Events note: a short
 * note on something that happened during the stay. Outpatient has no type.
 */
export const NOTE_FORMATS = {
  ADMISSION: "admission",
  PROGRESS: "progress",
  EVENTS: "events",
};

/** Unknown or missing values read as admission, which is how every older session was made. */
export const normalizeNoteFormat = (value) =>
  Object.values(NOTE_FORMATS).includes(value) ? value : NOTE_FORMATS.ADMISSION;

/** The Events note type: Event, Differential, Plan, Chart, with the events as the only source. */
export const isEventsNote = (setting, noteFormat) =>
  isInpatient(setting) && normalizeNoteFormat(noteFormat) === NOTE_FORMATS.EVENTS;

const eventLines = (events) =>
  normalizeInpatientEvents(events).map((event) => {
    const comment = event.comment.trim();
    const time = parseEventTime(event.occurredAt);
    const when = time ? ` (${format(time, "yyyy-MM-dd HH:mm")})` : "";
    return `- ${event.name}${when}${comment ? `: ${comment}` : ""}`;
  });

/**
 * On the Events note type there is no narrative: the events stand in for the transcript in the
 * differential, plan and note requests. Empty until there is an event.
 */
export const buildEventsSourceText = (events = []) => {
  const lines = eventLines(events);
  if (!lines.length) return "";
  return [
    "EVENTS DOCUMENTED BY THE PHYSICIAN:",
    "These events are the clinical source for this note. There is no separate narrative.",
    "",
    ...lines,
  ].join("\n");
};

/**
 * Added to the plan request on the Events note type. The plan there uses the base treatment
 * prompt, because the inpatient one writes a whole-day Daily Plan.
 */
export const EVENTS_PLAN_FOCUS = [
  "EVENTS NOTE FOCUS:",
  "Every diagnostic test and treatment must address the events above.",
  "Do not expand into a full admission workup or a hospital daily plan.",
].join("\n");

/** The Events note request beyond the events and diagnoses: the plan items the physician kept. */
export const buildEventsNoteContext = ({ events = [], planItems = [] } = {}) => {
  const blocks = [];
  const lines = eventLines(events);
  if (lines.length) blocks.push(`EVENTS:\n${lines.join("\n")}`);
  if (planItems.length) {
    blocks.push(`PLAN:\n${planItems.map((item) => `- ${item.name}`).join("\n")}`);
  }
  return blocks.join("\n\n");
};

/**
 * Sent only when the note falls back to the base prompt because chartmind-notes-inpatient is not
 * set up yet. Once it is, these instructions live in that prompt and the Query Manager.
 */
export const INPATIENT_FALLBACK_INSTRUCTIONS = [
  "INPATIENT NOTE INSTRUCTIONS:",
  "This is an inpatient encounter. Write the note the DOCUMENTATION OUTLINE names, not a clinic visit note. The transcript above is the physician's narrative.",
  "Use only what the physician provided. Do not add diagnoses, orders, or events that are not listed.",
  "DAILY PLAN lists only the items the physician checked. Write every one of them in the assessment and plan, with its result or comment when the physician entered one: a comment after a colon goes with the item, and a line that says reassuring means the physician found it reassuring. Do not drop an item or its comment.",
  "PENDING lists checked items that are still open. Put every one of them in the pending section. Also put there any Daily Plan item whose comment says it is still outstanding, for example \"due Tuesday\" or \"sent to outside lab\".",
  "EVENTS lists events the physician documented. Document each one in the hospital course, or in the interval history of a progress note. Use a time only when the physician's comment gives one.",
  "Organize the assessment and plan by problem.",
  "The SECTIONS line is the contract: return exactly those keys, even where an earlier JSON schema in this prompt names different ones.",
].join("\n");

/**
 * The Daily Plan, Pending and Events for the note request, as one block of text. Empty lists are
 * left out rather than sent as empty headings.
 *
 * @param {Object} params
 * @param {Array<{id: string, name: string}>} params.checkedItems - Daily Plan items checked
 * @param {Object<string, string>} params.comments - comment by item id
 * @param {Object<string, boolean>} params.reassuring - reassuring mark by item id
 * @param {Array} params.events - clinician-entered events
 */
export const buildInpatientNoteContext = ({
  checkedItems = [],
  comments = {},
  reassuring = {},
  events = [],
} = {}) => {
  const marks = { comments, reassuring };
  const blocks = [];

  if (checkedItems.length) {
    blocks.push(`DAILY PLAN:\n${checkedItems.map((item) => dailyPlanLine(item, marks)).join("\n")}`);
  }

  const pending = pendingDailyPlanNames(checkedItems, marks);
  if (pending.length) {
    blocks.push(`PENDING:\n${pending.map((name) => `- ${name}`).join("\n")}`);
  }

  const lines = eventLines(events);
  if (lines.length) blocks.push(`EVENTS:\n${lines.join("\n")}`);

  return blocks.join("\n\n");
};
